import {
  LayoutDashboard,
  Users as UsersIcon,
  MapPin,
  ScanLine,
  Route as RouteIcon,
  CalendarDays,
  Activity,
  FileBarChart2,
  MessageSquare,
  UserCog,
  CreditCard,
  ShieldCheck,
  Layers,
  Megaphone,
  Link2,
  KeyRound,
  ClipboardList,
  Inbox,
  Workflow,
  Brain,
  ShieldQuestion,
  EyeOff,
  Settings as SettingsIcon,
  type LucideIcon,
} from "lucide-react";
import { NAV_ITEMS as ASSET_NAV_ITEMS } from "@nexus/module-asset/frontend/routes";
import type { ModuleNavItem } from "@nexus/frontend-shared/nav";

// Module-contributed sidebar entries. One import per module; appended
// as a「模組」group at the end of NAV_GROUPS below. Each NavItem
// carries its own `permissions` (RequireRole-style ANY match) so
// filterNavByPermissions can gate without touching ROUTE_PERMISSIONS.
const MODULE_NAV_ITEMS: ModuleNavItem[] = [
  ...ASSET_NAV_ITEMS,
];

export type NavGroup = {
  label: string;
  items: NavItem[];
};
export type NavItem = {
  label: string;
  to: string;
  icon: LucideIcon;
  badge?: string;
  /** Module-contributed items carry their own permissions inline so
   *  filterNavByPermissions doesn't need a matching ROUTE_PERMISSIONS
   *  entry. Platform items still use the central ROUTE_PERMISSIONS
   *  map (kept as the source of truth for the rest of /dashboard/*). */
  permissions?: readonly string[];
};

export const NAV_GROUPS: NavGroup[] = [
  {
    label: "概覽",
    items: [
      { label: "總覽", to: "/dashboard", icon: LayoutDashboard },
      { label: "視覺總覽", to: "/dashboard/topology", icon: Workflow },
      { label: "即時監控", to: "/dashboard/monitor", icon: Activity, badge: "live" },
    ],
  },
  {
    label: "組織",
    items: [
      // 「客戶單位」 sidebar 條目移除 — super_admin 走 /dashboard/platform/tenants
      // (完整跨租戶管理工具)；tenant_admin 改自家名稱 / 品牌色到「個人設定」
      // 頁的「單位品牌」 區塊。
      { label: "終端客戶", to: "/dashboard/clients", icon: UsersIcon },
      { label: "案場", to: "/dashboard/sites", icon: MapPin },
    ],
  },
  {
    label: "巡邏設定",
    items: [
      { label: "地點", to: "/dashboard/locations", icon: ScanLine },
      { label: "路線", to: "/dashboard/routes", icon: RouteIcon },
      { label: "任務", to: "/dashboard/tasks", icon: ClipboardList },
      { label: "任務提交", to: "/dashboard/submissions", icon: Inbox },
      { label: "班表", to: "/dashboard/schedules", icon: CalendarDays },
    ],
  },
  {
    label: "營運",
    items: [
      { label: "報表中心", to: "/dashboard/reports", icon: FileBarChart2 },
      { label: "LINE 設定", to: "/dashboard/line", icon: MessageSquare },
    ],
  },
  {
    label: "帳號與系統",
    items: [
      // /dashboard/admin/binding-codes 與 /dashboard/devices 改由「使用者」
      // 頁面 inline 入口取代（邀請 modal + 抽屜內裝置 tab）；URL 保留為 audit。
      // 「AI 對話用量」 名稱跟 地點月結計費 區隔，避免被誤認為按 token 計費。
      { label: "使用者", to: "/dashboard/users", icon: UserCog },
      { label: "計費", to: "/dashboard/billing", icon: CreditCard },
      { label: "AI 對話用量", to: "/dashboard/ai-usage", icon: Activity },
      { label: "操作紀錄", to: "/dashboard/audit", icon: ShieldCheck },
      { label: "MCP 工具核准", to: "/dashboard/mcp-approvals", icon: ShieldQuestion },
      { label: "MCP 工具可見性", to: "/dashboard/mcp-tool-projections", icon: EyeOff },
      { label: "個人設定", to: "/dashboard/settings", icon: SettingsIcon },
    ],
  },
  {
    label: "權限管理",
    items: [
      // Custom RBAC 自訂角色頁 — spec docs/specs/custom-rbac.md §6.
      // tenant_admin 可建立 / 編輯 / 刪除自訂角色；auditor 唯讀；
      // manager 看不到（避免 manager 自己給自己加 billing:write.adjust）。
      { label: "角色與權限", to: "/dashboard/admin/roles", icon: ShieldCheck },
    ],
  },
  // Module-contributed sidebar entries. Aggregated from
  // modules/<id>/frontend/routes.tsx NAV_ITEMS exports. Each item's
  // own `permissions` array gates visibility (see
  // filterNavByPermissions). Empty group is hidden by the same
  // filter, so users without any module scope don't see this label.
  {
    label: "模組",
    items: MODULE_NAV_ITEMS.map((it) => ({
      label: it.label,
      to: it.to,
      icon: it.icon,
      badge: it.badge,
      permissions: it.permissions,
    })),
  },
  // super_admin 專用 — filterNavForRole() 會在非 super_admin 角色完全過濾掉。
  {
    label: "SENTRY 平台",
    items: [
      { label: "全部客戶單位",       to: "/dashboard/platform/tenants",       icon: Layers },
      { label: "全部使用者",     to: "/dashboard/platform/users",         icon: UsersIcon },
      { label: "系統狀態",   to: "/dashboard/platform/health",        icon: Activity },
      { label: "全平台公告",   to: "/dashboard/platform/announcements", icon: Megaphone },
      { label: "綁定我的 LINE",to: "/dashboard/platform/me/link-line",  icon: Link2 },
      { label: "平台金鑰",     to: "/dashboard/platform/secrets",       icon: KeyRound },
      { label: "模組啟用",     to: "/dashboard/platform/modules",       icon: Layers },
      { label: "角色範本庫",   to: "/dashboard/platform/role-templates",icon: ShieldCheck },
      { label: "共用技能庫",   to: "/dashboard/platform/shared-skills", icon: Brain },
    ],
  },
];

export function flatNav(): NavItem[] {
  return NAV_GROUPS.flatMap((g) => g.items);
}


// Per-route role allowlists. Mirror routes/dashboard/Shell.tsx + canonical
// `Rbac.DASHBOARD_ROUTE_ROLES` (packages/shared/src/rbac/index.ts).
export const ROUTE_ROLES: Record<string, readonly string[]> = {
  // tenant pages — super_admin can also enter every tenant page (impersonation).
  "/dashboard":                            ["tenant_admin", "manager", "officer", "auditor", "super_admin"],
  "/dashboard/monitor":                    ["tenant_admin", "manager", "auditor", "super_admin"],
  "/dashboard/clients":                    ["tenant_admin", "manager", "auditor", "super_admin"],
  "/dashboard/sites":                      ["tenant_admin", "manager", "auditor", "super_admin"],
  "/dashboard/locations":                ["tenant_admin", "manager", "auditor", "super_admin"],
  "/dashboard/routes":                     ["tenant_admin", "manager", "auditor", "super_admin"],
  "/dashboard/tasks":                      ["tenant_admin", "manager", "site_supervisor", "super_admin"],
  "/dashboard/submissions":                ["tenant_admin", "manager", "auditor", "site_supervisor", "super_admin"],
  "/dashboard/schedules":                  ["tenant_admin", "manager", "officer", "auditor", "super_admin"],
  "/dashboard/reports":                    ["tenant_admin", "manager", "auditor", "super_admin"],
  "/dashboard/line":                       ["tenant_admin", "manager", "super_admin"],
  "/dashboard/users":                      ["tenant_admin", "manager", "auditor", "super_admin"],
  "/dashboard/devices":                    ["tenant_admin", "manager", "super_admin"],
  "/dashboard/billing":                    ["tenant_admin", "auditor", "super_admin"],
  "/dashboard/ai-usage":                   ["tenant_admin", "auditor", "super_admin"],
  "/dashboard/audit":                      ["tenant_admin", "auditor", "super_admin"],
  // High-risk control-plane action — gated by mcp:approve_tool_call (held by
  // tenant_admin only by default). super_admin god-mode also passes.
  "/dashboard/mcp-approvals":              ["tenant_admin", "super_admin"],
  // Per-tenant MCP tool-visibility overrides — gated by mcp:manage_projection
  // (tenant_admin only by default); super_admin god-mode also passes.
  "/dashboard/mcp-tool-projections":       ["tenant_admin", "super_admin"],
  "/dashboard/admin/binding-codes":        ["tenant_admin", "manager", "auditor", "super_admin"],
  "/dashboard/admin/roles":                ["tenant_admin", "auditor", "super_admin"],
  "/dashboard/admin/roles/new":            ["tenant_admin", "super_admin"],
  // /settings is "your own settings" — every role with a session can use
  // it (binding LINE, future: notification prefs, password change).
  "/dashboard/settings":                   ["tenant_admin", "manager", "officer", "auditor", "site_supervisor", "control_center_operator", "client_viewer", "super_admin"],
  // Platform pages — super_admin only.
  "/dashboard/platform/tenants":           ["super_admin"],
  "/dashboard/platform/users":             ["super_admin"],
  "/dashboard/platform/health":            ["super_admin"],
  "/dashboard/platform/announcements":     ["super_admin"],
  "/dashboard/platform/me/link-line":      ["super_admin"],
  "/dashboard/platform/secrets":           ["super_admin"],
  "/dashboard/platform/role-templates":    ["super_admin"],
  "/dashboard/platform/shared-skills":     ["super_admin"],
  "/dashboard/platform/shared-skills/candidates": ["super_admin"],
};

// Per-route required permission ids (ANY match grants access). Empty list
// = any authenticated session passes. Mirror of `Rbac.DASHBOARD_ROUTE_PERMISSIONS`
// (packages/shared/src/rbac/index.ts) — keep in sync.
export const ROUTE_PERMISSIONS: Record<string, readonly string[]> = {
  "/dashboard":                            ["sites:read.list", "patrols:read.self", "patrols:read.list", "schedules:read.self", "incident:read.self", "ai:read.facts", "users:read.list"],
  "/dashboard/topology":                   ["sites:read.list"],
  "/dashboard/monitor":                    ["sites:read.list", "patrols:read.list"],
  "/dashboard/clients":                    ["clients:read.list"],
  "/dashboard/sites":                      ["sites:read.list"],
  "/dashboard/locations":                ["locations:read.list"],
  "/dashboard/routes":                     ["routes:read.list"],
  "/dashboard/tasks":                      ["tasks:read.list"],
  "/dashboard/submissions":                ["tasks:read.list", "patrols:read.list"],
  "/dashboard/schedules":                  ["schedules:read.list", "schedules:read.self"],
  "/dashboard/reports":                    ["reports:read.tenant", "reports:read.site", "reports:read.officer"],
  "/dashboard/line":                       ["line:execute.bind_group", "line:write.update_group"],
  "/dashboard/users":                      ["users:read.list"],
  "/dashboard/billing":                    ["billing:read.invoices", "billing:read.usage"],
  "/dashboard/ai-usage":                   ["billing:read.usage", "ai:read.usage"],
  "/dashboard/audit":                      ["audit:read.log"],
  "/dashboard/mcp-approvals":              ["mcp:approve_tool_call"],
  "/dashboard/mcp-tool-projections":       ["mcp:manage_projection"],
  "/dashboard/admin/binding-codes":        ["users:write.invite"],
  "/dashboard/admin/roles":                ["roles:read.list"],
  "/dashboard/admin/roles/new":            ["roles:write.create"],
  // Self-serve account settings — any authenticated user.
  "/dashboard/settings":                   [],
  // Platform pages — gated by impersonate permission (super_admin god mode).
  "/dashboard/platform/tenants":           ["platform:execute.impersonate"],
  "/dashboard/platform/users":             ["platform:execute.impersonate"],
  "/dashboard/platform/health":            ["platform:execute.impersonate"],
  "/dashboard/platform/announcements":     ["platform:execute.impersonate"],
  "/dashboard/platform/me/link-line":      ["platform:execute.impersonate"],
  "/dashboard/platform/secrets":           ["platform:execute.impersonate"],
  "/dashboard/platform/modules":           ["platform:execute.impersonate"],
  "/dashboard/platform/role-templates":    ["platform:execute.impersonate"],
  "/dashboard/platform/shared-skills":     ["platform:execute.impersonate"],
  "/dashboard/platform/shared-skills/candidates": ["platform:execute.impersonate"],
};

export function filterNavForRole(role: string, groups: NavGroup[] = NAV_GROUPS): NavGroup[] {
  const out: NavGroup[] = [];
  for (const g of groups) {
    const items = g.items.filter((it) => {
      const allow = ROUTE_ROLES[it.to];
      if (!allow) return true;             // unknown route — show by default
      return allow.includes(role);
    });
    if (items.length > 0) out.push({ label: g.label, items });
  }
  return out;
}

/** Permission-based sidebar filter — Custom RBAC §6 preferred path.
 *  Uses Rbac.DASHBOARD_ROUTE_PERMISSIONS as source of truth. Items whose
 *  route has empty required[] (e.g. /settings) always show. Unknown route
 *  → show (defensive default — same as filterNavForRole).
 *
 *  super_admin god-mode short-circuits ANY check (their session.permissions
 *  includes every permission so hasAnyPermission trivially passes). */
export function filterNavByPermissions(
  permissions: readonly string[],
  superAdmin: boolean,
  groups: NavGroup[] = NAV_GROUPS,
): NavGroup[] {
  if (superAdmin) return groups;
  const have = new Set(permissions);
  const out: NavGroup[] = [];
  for (const g of groups) {
    const items = g.items.filter((it) => {
      // Inline permissions (module-contributed items) take precedence
      // over the central ROUTE_PERMISSIONS map. Lets modules gate
      // visibility without touching shared code.
      const required = it.permissions ?? ROUTE_PERMISSIONS[it.to];
      if (!required) return true;          // unknown route — show by default
      if (required.length === 0) return true;
      return required.some((p) => have.has(p));
    });
    if (items.length > 0) out.push({ label: g.label, items });
  }
  return out;
}
